// Clinical-task query-key constants (MR-TASKS-BIRTH C2 + D2, OpenSpec
// `monorepo-tasks-data-core`). Definitions moved here verbatim from their hook
// modules (which import + re-export — the GET_USER pattern); the strings are
// cache identities and are FROZEN: renaming one orphans every user's query
// cache.
//
// C2 moved the first two; D2 (ARCFE-945) brought the session/inbox keys with
// their hooks — the session key's cohort-C hold resolved as P0.5 option 1
// (the app passes `isOfflineSession`; the hook moved parameterised) — plus the
// optimistic-create sentinel from `@/constants/clinical-tasks`.
import { type ClinicalTasksQueryParams } from './types';

/** List/filter cache — `useQueryClinicalTasks`. */
export const GET_CLINICAL_TASKS = 'get_clinical_tasks';

/** Single-task cache — `useQueryClinicalTaskById`. */
export const GET_CLINICAL_TASK_BY_ID = 'get_clinical_task_by_id';

/** Single prescription — `useQueryPrescription` / `useMutateCreatePrescription`. */
export const GET_PRESCRIPTION = 'get_prescription';

/** Per-session task list — `useQueryClinicalTasksBySessionId`. */
export const GET_CLINICAL_TASKS_BY_SESSION_ID = 'get_clinical_tasks_for_session';

/** Tasks linked to one inbox message — `useQueryClinicalTasksByInboxMessage`. */
export const GET_CLINICAL_TASKS_BY_INBOX_MESSAGE = 'get_clinical_tasks_for_inbox_message';

/**
 * Optimistic-create sentinel row id. The create form inserts a task with this
 * id; `useMutateCreateSessionClinicalTask` reconciles it with the POST result.
 * A stable identifier, not a cache key — but frozen for the same reason.
 */
export const CREATE_TEMP_CLINICAL_TASK_ID = 'shared-create-clinical-task';

/**
 * Key factories mirroring the exact shapes the hooks build today
 * (`[GET_CLINICAL_TASKS, params]`, `[GET_CLINICAL_TASK_BY_ID, taskId]`,
 * `[GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }]`,
 * `[GET_CLINICAL_TASKS_BY_INBOX_MESSAGE, { inboxMessageId }]`) so a consumer
 * can target a cache entry without re-deriving tuple layout.
 */
export const clinicalTaskKeys = {
  list: (params?: ClinicalTasksQueryParams) => [GET_CLINICAL_TASKS, params] as const,
  byId: (taskId: string | null) => [GET_CLINICAL_TASK_BY_ID, taskId] as const,
  bySession: (sessionId: string) =>
    [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }] as const,
  byInboxMessage: (inboxMessageId: string | undefined) =>
    [GET_CLINICAL_TASKS_BY_INBOX_MESSAGE, { inboxMessageId }] as const,
};
